import type { StatusRole } from '@lighthouse/design'
import { NOT_MEASURED, exactNumber, shortDuration } from '../../utils/format'
import { cartographerUrl } from '../../utils/cartographerUrl'
import type {
  ScheduleCostStatus, ScheduleDemandKind, ScheduleItemState, ScheduleLoadBanner, ScheduleLoadBucket,
  ScheduleLoadCost, ScheduleLoadItem, ScheduleLoadSeries, ScheduleLoadVerdict, ScheduleSeriesPlacement,
} from '../../types/scheduleLoad'

/**
 * Presentation for the schedule-load forecast (`/schedules-load`).
 *
 * Every helper here is a wording or a layout decision, never a calculation the
 * server already made. The verdicts, the simulation, the costs, the floors and
 * the per-bucket display offsets are all computed by `ScheduleLoadForecaster`
 * and `ScheduleCostModel`, each with its reviewer flaw recorded beside it, and a
 * client-side "correction" would reintroduce one. The page renders what arrives.
 *
 * Two rules run through the whole file:
 *
 *  - **A wall clock is read from the served string, never computed.** `start` and
 *    `startsAt` already carry the display zone's offset in force at that instant,
 *    so "15:00" is the text between the `T` and the offset. Adding one offset to
 *    UTC is the bug doc 11 §6.1 warns about: after a daylight-saving change every
 *    later quarter-hour sits an hour out. See {@link wallClock}.
 *  - **Null is unmeasured.** A duration, a ratio or a count the server withheld
 *    renders as `NOT_MEASURED`, and is tested with `== null` rather than `=== null`
 *    so a reinstated `WhenWritingNull` blanks it instead of inverting it.
 */

// ---------------------------------------------------------------------------
// The window
// ---------------------------------------------------------------------------

/**
 * The windows offered. 24 hours is the default because it answers the question
 * the page exists for — "200 reports fire at 3pm" — whatever the time now. A
 * week is the API's maximum and the only window in which a weekly schedule is
 * visible at all.
 */
export const WINDOW_OPTIONS = [6, 24, 48, 168] as const
export const DEFAULT_HOURS = 24

export function windowLabel(hours: number): string {
  return hours === 168 ? '7 days' : `${hours} hours`
}

/**
 * `?hours=` from the URL, or the default. Only an offered value is accepted:
 * the API refuses anything outside 1..168 with a 400 rather than clamping it,
 * and a hand-edited URL should land on a real window rather than an error page.
 */
export function parseHours(raw: string | null): number {
  const n = raw == null ? NaN : Number(raw)
  return (WINDOW_OPTIONS as readonly number[]).includes(n) ? n : DEFAULT_HOURS
}

/**
 * The browser's IANA zone, or null. Never a substitute.
 *
 * The API has no default zone and neither does this page: a forecast drawn in a
 * zone nobody asked for puts every spike in the wrong hour. A browser that
 * cannot name its zone gets a page that says so, not a quiet "UTC".
 */
export function browserZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return zone && zone.trim() !== '' ? zone : null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Clocks
// ---------------------------------------------------------------------------

/** The wall-clock parts of an ISO instant, exactly as the server wrote them. */
export interface WallClock {
  /** `YYYY-MM-DD` in the zone of the string's own offset. */
  date: string
  /** `HH:mm`. */
  time: string
  hour: number
  minute: number
  offsetMinutes: number
}

const ISO_WITH_OFFSET = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/

/**
 * Reads the wall clock out of a served instant rather than recomputing it.
 *
 * `2026-11-01T01:15:00-08:00` is 01:15 on 1 November at UTC−08:00, and that is
 * the server's answer — computed per instant from the display zone, so the two
 * different 01:15s of a fall-back night arrive with different offsets and stay
 * distinguishable. Parsing the string keeps that answer intact; building a
 * `Date` and formatting it would re-derive the zone in the browser, whose zone
 * database can disagree with the server's.
 *
 * Null for anything that is not an ISO instant with an explicit offset — an
 * offset-free string would be read by a browser as its own local time, which is
 * the bug the API's `Utc()` stamping exists to prevent.
 */
export function wallClock(iso: string | null): WallClock | null {
  if (iso == null) return null
  const match = ISO_WITH_OFFSET.exec(iso)
  if (!match) return null
  const [, date, hh, mm, offset] = match
  let offsetMinutes = 0
  if (offset !== 'Z') {
    const sign = offset.startsWith('-') ? -1 : 1
    offsetMinutes = sign * (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(4, 6)))
  }
  return { date, time: `${hh}:${mm}`, hour: Number(hh), minute: Number(mm), offsetMinutes }
}

/** "UTC−07:00", with a true minus sign; "UTC+00:00" for zero. */
export function offsetLabel(minutes: number | null): string {
  if (minutes == null) return NOT_MEASURED
  const sign = minutes < 0 ? '−' : '+'
  const magnitude = Math.abs(minutes)
  const hh = String(Math.floor(magnitude / 60)).padStart(2, '0')
  const mm = String(magnitude % 60).padStart(2, '0')
  return `UTC${sign}${hh}:${mm}`
}

/**
 * "Thu 24 Sep" for a `YYYY-MM-DD` date.
 *
 * Formatted at noon UTC, IN UTC, because the date is already a wall-clock date
 * in the display zone; formatting it in the browser's zone could move it a day.
 */
export function dayLabel(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return NOT_MEASURED
  return parsed.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** "15:00" — the served wall clock alone, or a dash. */
export function timeLabel(iso: string | null): string {
  return wallClock(iso)?.time ?? NOT_MEASURED
}

/** "15:00 Thu 24 Sep (UTC−07:00)" — the served wall clock with its day and offset. */
export function atLabel(iso: string | null): string {
  const wall = wallClock(iso)
  if (!wall) return NOT_MEASURED
  return `${wall.time} ${dayLabel(wall.date)} (${offsetLabel(wall.offsetMinutes)})`
}

/**
 * An instant's wall clock in ANOTHER zone — used only for the worker's zone,
 * which the server does not render.
 *
 * This is the one place the browser's zone database is consulted, and it is
 * for a label, not for placement: it answers "what did Automaton register this
 * as", beside the server's own display-zone time. Null when the zone is absent
 * or the browser does not know it.
 */
export function wallClockIn(utcIso: string, ianaId: string | null): string | null {
  if (!ianaId) return null
  const instant = Date.parse(utcIso)
  if (Number.isNaN(instant)) return null
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: ianaId, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).format(instant)
  } catch {
    return null
  }
}

/**
 * An instant INSIDE one quarter-hour, on that quarter-hour's own served offset —
 * "15:03:20".
 *
 * For the few instants the server serves in UTC only (`peakDemandAt`). This is
 * the per-bucket offset build plan #8 asks for, not "UTC plus one offset for the
 * window": every UTC quarter-hour carries its own `displayOffsetMinutes`, and an
 * offset never changes inside one, because zones change offset on the hour or
 * half-hour and every offset in use is a multiple of fifteen minutes.
 */
export function clockAtOffset(utcIso: string | null, offsetMinutes: number): string {
  if (utcIso == null) return NOT_MEASURED
  const instant = Date.parse(utcIso)
  if (Number.isNaN(instant)) return NOT_MEASURED
  const shifted = new Date(instant + offsetMinutes * 60_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}:${pad(shifted.getUTCSeconds())}`
}

/** Whether two ISO strings are the same instant, whatever their offsets or precision. */
export function sameInstant(a: string | null, b: string | null): boolean {
  if (a == null || b == null) return false
  const x = Date.parse(a)
  const y = Date.parse(b)
  return !Number.isNaN(x) && x === y
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * A duration in seconds, or a dash.
 *
 * Sub-ten-second values keep a decimal. A report query that takes 0.4 s must not
 * print as "0s", which reads as free — the one reading §6.3 forbids for an
 * unknown, and no less wrong for a measured small cost.
 */
export function durationLabel(seconds: number | null): string {
  if (seconds == null) return NOT_MEASURED
  if (seconds < 10) return `${Math.max(0, seconds).toFixed(1)}s`
  return shortDuration(seconds)
}

/** "12,300 slot-s", or a dash. Slot-seconds are the page's unit of demand (§6.2). */
export function slotSecondsLabel(value: number | null): string {
  return value == null ? NOT_MEASURED : `${exactNumber(Math.round(value))} slot-s`
}

/**
 * A served ratio as a percentage, or a dash.
 *
 * The server withholds these as null when capacity is unmeasured, so a dash is
 * the only honest rendering — never "0%", which reads as idle.
 */
export function ratioPercent(ratio: number | null): string {
  return ratio == null ? NOT_MEASURED : `${Math.round(ratio * 100)}%`
}

/** "2.5×" — peak demand against live slots. A dash when unmeasured. */
export function timesLabel(ratio: number | null): string {
  return ratio == null ? NOT_MEASURED : `${ratio.toFixed(1)}×`
}

/** "13.7" — an average number of slots wanted. Always one decimal so columns line up. */
export function concurrencyLabel(value: number): string {
  return value.toFixed(1)
}

/** A count that can be withheld, or a dash. */
export function countLabel(value: number | null): string {
  return value == null ? NOT_MEASURED : exactNumber(value)
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${exactNumber(n)} ${n === 1 ? one : many}`
}

// ---------------------------------------------------------------------------
// Verdicts
// ---------------------------------------------------------------------------

/** The server's severity order, most feared first, for the legend and the tallies. */
export const VERDICT_ORDER: ScheduleLoadVerdict[] = [
  'Unmeasured', 'Overloaded', 'Indeterminate', 'Contended', 'AtRisk', 'Clear',
]

/**
 * The status role a verdict may wear, or null.
 *
 * `Indeterminate` and `Unmeasured` get NO role, on purpose. Both mean "this page
 * cannot say", and every role in the scale — including the mildest — is a claim
 * about the load. `Indeterminate` especially must not borrow a warning tone: it
 * covers "199 unknowns and one estimate", where a warning would still be a
 * judgement the numbers do not support. They render in neutral ink with their
 * own glyphs instead (see `VerdictBadge`).
 *
 * `Clear` wears `good` because it is earned: the server only says Clear when
 * nothing is doubtful and nobody waits even at the slowest observed durations.
 * Its scope — scheduled demand only — is printed beside every verdict block.
 */
export function verdictRole(verdict: ScheduleLoadVerdict | null): StatusRole | null {
  switch (verdict) {
    case 'Overloaded': return 'critical'
    case 'Contended': return 'serious'
    case 'AtRisk': return 'warning'
    case 'Clear': return 'good'
    default: return null
  }
}

export function verdictLabel(verdict: ScheduleLoadVerdict | null): string {
  if (verdict == null) return NOT_MEASURED
  return verdict === 'AtRisk' ? 'At risk' : verdict
}

/** What each verdict means, in the server's own terms (`ScheduleLoadVerdict` remarks). */
export function verdictMeaning(verdict: ScheduleLoadVerdict): string {
  switch (verdict) {
    case 'Unmeasured':
      return 'No current capacity measurement, so nothing is judged against a slot count. Demand is still shown.'
    // Worded from the forecaster's code, not the enum's doc comment: the verdict
    // is `BacklogAtEnd > 0` over EVERYTHING waiting, so a quarter-hour with no
    // arrivals of its own reads Overloaded while an earlier burst is still queued.
    case 'Overloaded':
      return 'Something is still waiting for a slot when this quarter-hour ends — the queue does not drain within it.'
    case 'Indeterminate':
      return 'The known demand is a floor: something here has no estimate, a floor cost, a cut-short series, or an hour the page cannot compute.'
    case 'Contended':
      return 'Something waits for a slot during this quarter-hour, and the queue drains before it ends.'
    case 'AtRisk':
      return 'Clear at the planning figure (p90), but contended if the slowest observed runs recur.'
    case 'Clear':
      return 'Nobody waits, even at the slowest observed durations — for the scheduled demand this page can see.'
  }
}

/** A `VerdictReasons` code, as a sentence. Unknown codes are shown by name rather than dropped. */
export function reasonText(code: string): string {
  switch (code) {
    case 'NoCapacityMeasurement':
      return 'No live slot count is measured, so this quarter-hour is not judged against capacity.'
    case 'BacklogAtEnd':
      return 'Work is still waiting for a slot when this quarter-hour ends (planning figures) — whether it arrived here or earlier.'
    case 'WaitsForSlot':
      return 'Something waits for a slot during this quarter-hour (planning figures).'
    case 'SlowestObservedContends':
      return 'At the slowest observed durations, something would wait for a slot.'
    case 'UnestimatedStarts':
      return 'Something starting here has no cost estimate, so it holds no slot time in the simulation.'
    case 'FloorCostStarts':
      return 'Something starting here is costed from history that excludes failed attempts — a floor.'
    case 'UnknownPossiblyRunning':
      return 'Work of unknown cost that started earlier may still be running here.'
    case 'IncompleteSeries':
      return 'A series stopped expanding before this quarter-hour ended, so firings here may be missing.'
    case 'UnplaceableWillFire':
      return 'A job will fire at an hour this page cannot compute, so no quarter-hour can read clear.'
    default:
      return humanizeCode(code)
  }
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

/** Mantine colour for a served banner severity. An unknown severity is shown, not dropped. */
export function bannerColor(severity: string): string {
  switch (severity) {
    case 'critical': return 'red'
    case 'serious': return 'orange'
    case 'warning': return 'yellow'
    default: return 'blue'
  }
}

/**
 * Headline banners (shown one alert each) versus context (grouped into one).
 *
 * Every banner is shown — the server orders them "most severe first" and the
 * page keeps that order — but eight info lines as eight alerts would bury the
 * one critical line above them, which is the opposite of what an ordering is for.
 */
export function splitBanners(banners: ScheduleLoadBanner[]): {
  headline: ScheduleLoadBanner[]
  context: ScheduleLoadBanner[]
} {
  return {
    headline: banners.filter((b) => b.severity !== 'info'),
    context: banners.filter((b) => b.severity === 'info'),
  }
}

/** "Worker zone not published" from "WorkerZoneNotPublished". */
export function humanizeCode(code: string): string {
  const words = code.replace(/([a-z0-9])([A-Z])/g, '$1 $2').split(' ')
  return words.map((w, i) => (i === 0 ? w : w.toLowerCase())).join(' ')
}

// ---------------------------------------------------------------------------
// Series and items
// ---------------------------------------------------------------------------

export function kindLabel(kind: ScheduleDemandKind): string {
  switch (kind) {
    case 'ReportDelivery': return 'Report delivery'
    case 'Warehouse': return 'Warehouse load'
    case 'Entity': return 'Entity load'
    case 'Maintenance': return 'Maintenance job'
    case 'Diagnostics': return 'Diagnostics job'
  }
}

export function isReport(kind: ScheduleDemandKind): boolean {
  return kind === 'ReportDelivery'
}

export function itemStateLabel(state: ScheduleItemState): string {
  switch (state) {
    case 'Scheduled': return 'Scheduled'
    case 'PendingTrigger': return 'Requested, not yet picked up'
    case 'CompletedPerRunRecord': return 'Already delivered'
    case 'SupersededByRunLedger': return 'Superseded by the run ledger'
    case 'WillRefuseToStart': return 'Will refuse to start — ETL halted'
    case 'AwaitingRetry': return 'Failed — being retried'
    case 'RetryAttempt': return 'Retry of a failed delivery — timing not knowable'
  }
}

export function placementLabel(placement: ScheduleSeriesPlacement): string {
  switch (placement) {
    case 'Placed': return 'Placed'
    case 'ZoneNotPublished': return 'Worker zone not published'
    case 'ZoneIsLocal': return 'Zone is "Local"'
    case 'ZoneBlank': return 'Zone is blank'
    case 'ZoneUnrecognised': return 'Zone not recognised'
    case 'CronUnparseable': return 'Cron does not parse'
  }
}

/**
 * What an occurrence holds a slot for, in words.
 *
 * "Unknown" is a word, never a zero or a dash: a dash would read as "not
 * measured yet" about a cost that has no history to measure, and zero is the
 * reading that makes a 200-report spike look safe (§6.3). A floor cost says
 * "at least", because failed attempts wrote no history and the real figure is
 * higher by an amount nobody recorded.
 */
export function itemCostLabel(item: Pick<ScheduleLoadItem, 'costStatus' | 'planningSeconds' | 'isFloorCost'>): string {
  if (item.costStatus === 'Unknown' || item.planningSeconds == null) return 'Unknown'
  const planning = durationLabel(item.planningSeconds)
  return item.isFloorCost ? `at least ${planning}` : planning
}

/** The three nearest-rank figures of a series in one line, or "Unknown". */
export function costSummary(cost: ScheduleLoadCost): string {
  if (cost.status === 'Unknown') return 'Unknown'
  return `p50 ${durationLabel(cost.p50Seconds)} · p90 ${durationLabel(cost.p90Seconds)} · max ${durationLabel(cost.maxSeconds)}`
}

export function costStatusLabel(status: ScheduleCostStatus): string {
  return status === 'Estimated' ? 'Estimated from its own history' : 'Unknown'
}

/** How the ETL in-flight rows are named. The kind strings are the server's. */
export function inFlightKindLabel(kind: string): string {
  switch (kind) {
    case 'EtlRun': return 'ETL run'
    case 'DataCombRun': return 'Data Comb pass'
    case 'UnexplainedBusySlots': return 'Busy slots the model cannot explain'
    case 'UnexplainedQueuedJobs': return 'Queued jobs the model cannot explain'
    default: return humanizeCode(kind)
  }
}

/** The worker zone's `problem`, as a sentence, or null when there is none. */
export function workerZoneProblemText(problem: string | null): string | null {
  if (problem == null) return null
  switch (problem) {
    case 'NotPublished':
      return 'Automaton has not published the zone it schedules in: every catalogue row predates the zone column. Apply the migration and restart Automaton.'
    case 'Blank':
      return 'Automaton published a blank zone. It is not read as UTC, so its jobs cannot be placed.'
    case 'Local':
      return 'Automaton published the literal "Local": its host could not name its own zone, and resolving "Local" here would give this API host\'s zone instead.'
    case 'Unrecognised':
      return 'The API host does not recognise the zone Automaton published, so its jobs cannot be placed.'
    default:
      return humanizeCode(problem)
  }
}

/**
 * The first LISTED occurrence of every series in the window.
 *
 * "Listed", not "next": the server caps items per bucket and per response, so a
 * series whose earliest firing fell into an over-full bucket can have its first
 * listed occurrence later than its first real one. Callers say "first listed".
 */
export function firstListedBySeries(buckets: ScheduleLoadBucket[]): Map<string, ScheduleLoadItem> {
  const first = new Map<string, ScheduleLoadItem>()
  for (const bucket of buckets) {
    for (const item of bucket.items) {
      if (!first.has(item.seriesKey)) first.set(item.seriesKey, item)
    }
  }
  return first
}

/**
 * A worked example of the worker-zone gap, from a real job in this window.
 *
 * Picks the placed non-report series whose first listed occurrence is EARLIEST
 * — in production that is the head of the maintenance chain, the job an
 * operator is most likely to be surprised by — and renders that occurrence
 * twice: as Automaton registered it (in the worker's zone) and as it lands on
 * this page (the server's display-zone time). This is the whole of fact 2 on one
 * line — "02:30" in production is 02:30 UTC, which is the previous evening in
 * Pacific time — and it is computed from this response rather than asserted.
 */
export function workerZoneExample(
  series: ScheduleLoadSeries[],
  firstListed: Map<string, ScheduleLoadItem>,
  workerIanaId: string | null,
): { name: string; cron: string; workerTime: string; displayTime: string } | null {
  if (!workerIanaId) return null
  let best: { series: ScheduleLoadSeries; item: ScheduleLoadItem; at: number } | null = null
  for (const s of series) {
    if (isReport(s.kind) || s.placement !== 'Placed') continue
    const item = firstListed.get(s.seriesKey)
    const at = item ? Date.parse(item.startsAtUtc) : NaN
    if (!item || Number.isNaN(at)) continue
    if (!best || at < best.at) best = { series: s, item, at }
  }
  if (!best) return null
  const workerTime = wallClockIn(best.item.startsAtUtc, workerIanaId)
  if (!workerTime) return null
  return {
    name: best.series.name, cron: best.series.cronDescription, workerTime, displayTime: atLabel(best.item.startsAt),
  }
}

// ---------------------------------------------------------------------------
// Quarter-hours
// ---------------------------------------------------------------------------

/**
 * A quarter-hour where something starts or waits.
 *
 * Deliberately NOT "any quarter-hour that is not Clear". Unknown-cost jobs keep
 * every bucket after them Indeterminate until their horizon ends, and while
 * Automaton's zone is unpublished every bucket in the window is Indeterminate —
 * so "not Clear" would list the whole window and filter nothing. The table says
 * how many it leaves out and offers all of them; the timeline shows every one.
 */
export function isBusyBucket(bucket: ScheduleLoadBucket): boolean {
  return bucket.starts > 0
    || bucket.verdict === 'Overloaded'
    || bucket.verdict === 'Contended'
    || bucket.verdict === 'AtRisk'
    || (bucket.peakQueueLength ?? 0) > 0
    || (bucket.backlogAtEnd ?? 0) > 0
}

/**
 * Whether a quarter-hour's demand and occupancy figures are floors.
 *
 * Exactly the server's own "doubtful" test (`ScheduleLoadForecaster`, the
 * `doubtful` sum), read from the counts it serves — not a verdict recomputed.
 * An unestimated or possibly-still-running item holds NO slot time in the
 * simulation, so a quarter-hour of 200 unknowns serves "0 slot-s" and "0%".
 * Printed bare, those read as idle; the table marks them "≥" instead.
 */
export function isFloorBucket(bucket: ScheduleLoadBucket): boolean {
  return bucket.unestimatedStarts + bucket.floorCostStarts + bucket.unknownPossiblyRunning
    + bucket.incompleteSeriesCount + bucket.unplaceableWillFire > 0
}

/** "≥ 12,660 slot-s" when the figure is a floor, else the figure alone. */
export function floorMark(label: string, isFloor: boolean): string {
  return isFloor && label !== NOT_MEASURED ? `≥ ${label}` : label
}

/** One row of the timeline: one display date at one UTC offset. */
export interface TimelineRow {
  key: string
  date: string | null
  offsetMinutes: number | null
  /** `index` is the bucket's position in the served array — the timeline's keyboard order. */
  cells: { column: number; index: number; bucket: ScheduleLoadBucket }[]
}

/**
 * The buckets as rows of a 96-column wall-clock grid.
 *
 * One row per (display date, UTC offset), not per date, and the reason is the
 * fall-back night. 01:00–01:45 happens twice that night, once at each offset,
 * and a grid keyed by wall clock alone would draw the second hour on top of the
 * first — hiding a quarter-hour's demand behind another's. Splitting the row at
 * the offset change keeps every bucket visible and every row's wall clock
 * unique; the spring-forward night simply has a gap where 02:00 never happens.
 * Columns are wall-clock positions, so "15:00" is the same column on every row
 * and a daily spike lines up down the page.
 */
export function timelineRows(buckets: ScheduleLoadBucket[]): TimelineRow[] {
  const rows: TimelineRow[] = []
  buckets.forEach((bucket, index) => {
    const wall = wallClock(bucket.start)
    const key = wall ? `${wall.date}|${wall.offsetMinutes}` : 'unparsed'
    let row = rows.length > 0 && rows[rows.length - 1].key === key ? rows[rows.length - 1] : undefined
    if (!row) {
      row = { key, date: wall?.date ?? null, offsetMinutes: wall?.offsetMinutes ?? null, cells: [] }
      rows.push(row)
    }
    const column = wall ? wall.hour * 4 + Math.floor(wall.minute / 15) : index % 96
    row.cells.push({ column, index, bucket })
  })
  return rows
}

/** The accessible name of one timeline cell — everything the cell's colour stands for, in words. */
export function cellDescription(bucket: ScheduleLoadBucket): string {
  const parts = [
    `Quarter-hour from ${atLabel(bucket.start)}`,
    verdictLabel(bucket.verdict),
    plural(bucket.starts, 'start'),
  ]
  if (bucket.unestimatedStarts > 0) parts.push(`${exactNumber(bucket.unestimatedStarts)} with no estimate`)
  if (bucket.floorCostStarts > 0) parts.push(`${exactNumber(bucket.floorCostStarts)} at a floor cost`)
  if (bucket.unknownPossiblyRunning > 0) {
    parts.push(`${exactNumber(bucket.unknownPossiblyRunning)} of unknown cost possibly still running`)
  }
  if (bucket.verdict === 'Indeterminate') parts.push(`known demand alone: ${verdictLabel(bucket.floorVerdict)}`)
  return parts.join(', ')
}

// ---------------------------------------------------------------------------
// The Town Crier hand-off
// ---------------------------------------------------------------------------

/**
 * Town Crier's absolute URL, or null when this build does not know where
 * Cartographer is.
 *
 * A delegate: the origin, the path guard and the dev fallback live in
 * `utils/cartographerUrl.ts`, which governance, the report registry and
 * retirement also hand off through. Null is rendered as a named destination,
 * never as a link (see TownCrierLink.tsx).
 */
export function townCrierUrl(path: string): string | null {
  return cartographerUrl(path)
}
